import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { useHasAnyFormQuestionInsights } from '../../../../forms-page-components/useGetFormQuestionInsightCountByFormTemplate'
import { useFormBuilderLdFlags } from '../../../../launch-darkly'
import { GUIDED_SUCCESS_URL } from './constants'

// We use container queries here because we're in a variable width section on the dashboard (sidebar can be collapsed)
const VERTICAL_LAYOUT_BREAKPOINT = 600
const CTA_STACK_BREAKPOINT = 400

export const DashboardBanner = () => {
  const { t } = useTranslation('form-builder', { keyPrefix: 'question-insights.banner' })
  const hasInsights = useHasAnyFormQuestionInsights()
  const { SHOW_FORM_TEMPLATES_GUIDED_SUCCESS_BANNERS } = useFormBuilderLdFlags()
  const { FORM_INSIGHTS_AI } = useFeatureFlags()
  const { user } = useAuthenticatedUser()

  if (!hasInsights || !SHOW_FORM_TEMPLATES_GUIDED_SUCCESS_BANNERS || !FORM_INSIGHTS_AI || !user.isAdmin) {
    return null
  }

  return (
    <Paper sx={{ maxWidth: '100%', p: 5, mb: 3, containerType: 'inline-size' }}>
      <Stack
        direction="row"
        gap={2}
        sx={(theme) => ({
          [theme.containerQueries.down(VERTICAL_LAYOUT_BREAKPOINT)]: {
            flexDirection: 'column-reverse',
          },
        })}
      >
        <Stack sx={{ minWidth: '50%' }}>
          <Typography variant="upsized-semiBold" sx={{ lineHeight: 1.55 }}>
            {t('title')}
          </Typography>
          <Typography variant="body-regular" sx={{ lineHeight: 1.45 }}>
            {t('dashboard.subtitle')}
          </Typography>

          <Box flexGrow={1} />

          <Stack
            direction="row"
            gap={2}
            pt={2}
            sx={(theme) => ({
              [theme.containerQueries.down(CTA_STACK_BREAKPOINT)]: {
                flexDirection: 'column',
              },
            })}
          >
            <InternalLinkButton variant="contained" color="primary" href="/settings/forms">
              {t('view-insights')}
            </InternalLinkButton>
            <Button href={GUIDED_SUCCESS_URL} target="_blank" rel="noopener noreferrer" variant="text">
              {t('learn-more')}
            </Button>
          </Stack>
        </Stack>

        <Box flexGrow={1} />

        <Box
          component="img"
          src="/app/assets/insights-dashboard-banner.png"
          sx={(theme) => ({
            maxWidth: `min(${VERTICAL_LAYOUT_BREAKPOINT}px, 50%)`,
            [theme.containerQueries.down(VERTICAL_LAYOUT_BREAKPOINT)]: {
              maxWidth: '100%',
              height: 100,
            },
            // When we get really small, the image at 100px high looks a bit overly zoomed, so go even smaller
            [theme.containerQueries.down(CTA_STACK_BREAKPOINT)]: {
              height: 80,
            },
            minWidth: 0,
            borderRadius: 2,
            border: 1,
            borderColor: 'border.subtle',
            objectFit: 'cover',
          })}
        />
      </Stack>
    </Paper>
  )
}
